import React from 'react';
import styled, { css } from 'styled-components';

import { PlainButton } from '../../components/pageStyles';
import { tint, wz, wzType } from '../wizardStyles';

// 6778:17958 (type, flat fill, Bold label) and 6778:18182 (objective / action, gradient fill,
// Semi Bold label): 168.8 tall, r14, an icon chip of 46 at r12.
const Tile = styled(PlainButton)<{ $selected: boolean; $flat: boolean }>`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 168.8px;
  padding: 31px 18px;
  border-radius: 14px;
  text-align: center;
  color: ${wz.text};
  font-weight: ${({ $flat }) => ($flat ? 700 : 600)};
  ${wzType.tile}

  ${({ $selected, $flat }) =>
    $selected
      ? css`
          border: ${$flat ? '1px' : wz.line} solid ${wz.accent};
          background: ${tint(wz.accent, 0.14)};
        `
      : css`
          border: ${$flat ? '1px' : wz.line} solid ${wz.border};
          background: ${$flat
            ? wz.field
            : `linear-gradient(180deg, ${wz.field} 0%, ${wz.fieldEnd} 100%)`};
        `}

  > * + * {
    margin-top: 14px;
  }

  &:focus-visible {
    outline: 2px solid ${wz.accent};
    outline-offset: 2px;
  }
`;

const Chip = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: ${({ $selected }) => ($selected ? wz.accent : tint(wz.accent, 0.14))};
  color: ${({ $selected }) => ($selected ? wz.page : wz.accent)};
  font-size: 24px;
`;

interface ISelectionTileProps {
  icon: React.ReactNode;
  label: string;
  selected: boolean;
  onSelect: () => void;
  /** The type picker's flat tile; the later steps draw a gradient. */
  flat?: boolean;
  testId?: string;
}

const SelectionTile: React.FC<ISelectionTileProps> = ({
  icon,
  label,
  selected,
  onSelect,
  flat = false,
  testId
}) => (
  <Tile
    type="button"
    aria-pressed={selected}
    $selected={selected}
    $flat={flat}
    onClick={onSelect}
    data-testid={testId}
  >
    <Chip $selected={selected} aria-hidden="true">
      {icon}
    </Chip>
    <span>{label}</span>
  </Tile>
);

// A row of tiles 16 apart. `$fixed` tiles keep their drawn width and shrink to fit when there are
// more of them than drawn (01 draws four type tiles at 250); `$columns` wraps into a grid (6778:22346).
export const TileRow = styled.div<{ $fixed?: number; $columns?: number }>`
  display: ${({ $columns }) => ($columns ? 'grid' : 'flex')};
  justify-content: center;
  width: 100%;
  gap: 16px;
  ${({ $columns }) =>
    $columns ? `grid-template-columns: repeat(${$columns}, minmax(0, 1fr));` : ''}

  > * {
    ${({ $fixed }) => ($fixed ? `flex: 0 1 ${$fixed}px;` : '')}
  }
`;

export default SelectionTile;
